import { logoutPrimaryHttpSession } from "~/environments/primary/logout";
import { resolvePrimaryEnvironmentHttpUrl } from "~/environments/primary/target";
import { clearCloudAuthFailure } from "~/rpc/wsConnectionState";
import { isCmmdEmbedMode, withCmmdEmbedQuery } from "../embed/cmmdEmbedMode";

export class ForgeAuthExpiredError extends Error {
  constructor(message = "Session has expired. Please re-login.") {
    super(message);
    this.name = "ForgeAuthExpiredError";
  }
}

export function isForgeAuthExpiredError(error: unknown): error is ForgeAuthExpiredError {
  return (
    error instanceof ForgeAuthExpiredError ||
    (error instanceof Error && error.name === "ForgeAuthExpiredError")
  );
}

/**
 * sessionStorage key written on auth-expired redirect. Consumers must use
 * {@link takeAuthExpiredReason} (destructive, one-shot) or
 * {@link peekAuthExpiredReason} (non-destructive) rather than reading
 * sessionStorage directly.
 */
export const AUTH_EXPIRED_REASON_KEY = "forge_auth_expired_reason";
const AUTH_EXPIRED_REASON_VALUE = "session-expired";
const LEGACY_AUTH_EXPIRED_REASON_PARAM = "reason";
const LEGACY_CMMD_AUTH_EXPIRED_REASON = "cmmd-auth-expired";

export type ForgeAuthRedirectReason =
  | "session-expired"
  | "desktop-session-required"
  | "desktop-principal-mismatch"
  | "desktop-state-unavailable";

const AUTH_REDIRECT_REASON_MESSAGES = {
  "session-expired": "Session has expired. Please re-login.",
  "desktop-session-required":
    "Forge Desktop needs a CMMD sign-in before this device can host or control runtimes.",
  "desktop-principal-mismatch":
    "Forge Desktop and the local Forge session are signed in to different CMMD accounts. Sign in again to reconcile them.",
  "desktop-state-unavailable":
    "Forge Desktop could not verify its native CMMD session. Restart Forge or sign in again.",
} satisfies Readonly<Record<ForgeAuthRedirectReason, string>>;

export function resolveAuthRedirectReasonMessage(reason: string | null): string | undefined {
  if (reason === null || !(reason in AUTH_REDIRECT_REASON_MESSAGES)) {
    return undefined;
  }
  return AUTH_REDIRECT_REASON_MESSAGES[reason as ForgeAuthRedirectReason];
}

export function storeAuthRedirectReason(reason: ForgeAuthRedirectReason): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(AUTH_EXPIRED_REASON_KEY, reason);
  } catch {
    // Best-effort; the auth gate still fails closed without the display hint.
  }
}

/**
 * Non-destructive: returns the stored auth-expired reason without clearing it.
 * Use this for GATE checks that must not consume the toast signal.
 */
function readLegacyAuthExpiredReasonFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const currentUrl = new URL(window.location.href);
    return currentUrl.searchParams
      .getAll(LEGACY_AUTH_EXPIRED_REASON_PARAM)
      .includes(LEGACY_CMMD_AUTH_EXPIRED_REASON)
      ? AUTH_EXPIRED_REASON_VALUE
      : null;
  } catch {
    return null;
  }
}

function stripLegacyAuthExpiredReasonFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const currentUrl = new URL(window.location.href);
    const reasonValues = currentUrl.searchParams.getAll(LEGACY_AUTH_EXPIRED_REASON_PARAM);
    if (!reasonValues.includes(LEGACY_CMMD_AUTH_EXPIRED_REASON)) {
      return;
    }
    currentUrl.searchParams.delete(LEGACY_AUTH_EXPIRED_REASON_PARAM);
    for (const reasonValue of reasonValues) {
      if (reasonValue !== LEGACY_CMMD_AUTH_EXPIRED_REASON) {
        currentUrl.searchParams.append(LEGACY_AUTH_EXPIRED_REASON_PARAM, reasonValue);
      }
    }
    window.history.replaceState(
      {},
      typeof document === "undefined" ? "" : document.title,
      currentUrl.toString(),
    );
  } catch {
    // Best-effort URL cleanup. The auth signal still flows through storage.
  }
}

function consumeLegacyAuthExpiredReasonFromUrl(): string | null {
  const reason = readLegacyAuthExpiredReasonFromUrl();
  if (reason !== null) {
    stripLegacyAuthExpiredReasonFromUrl();
  }
  return reason;
}

function preserveLegacyAuthExpiredReasonForOneShot(): string | null {
  const reason = consumeLegacyAuthExpiredReasonFromUrl();
  if (reason === null || typeof window === "undefined") return reason;
  try {
    window.sessionStorage.setItem(AUTH_EXPIRED_REASON_KEY, reason);
  } catch {
    // Best-effort; callers still receive the normalized reason.
  }
  return reason;
}

export function clearAuthExpiredReason(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(AUTH_EXPIRED_REASON_KEY);
  } catch {
    // Best-effort; URL cleanup still matters when storage is unavailable.
  }
  stripLegacyAuthExpiredReasonFromUrl();
}

/**
 * Non-destructive: returns the stored auth-expired reason without clearing it.
 * Use this for GATE checks that must not consume the toast signal. Legacy URL
 * reasons are normalized into the one-shot storage key before the sticky query
 * param is stripped, preserving the later destructive read.
 */
export function peekAuthExpiredReason(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return (
      window.sessionStorage.getItem(AUTH_EXPIRED_REASON_KEY) ??
      preserveLegacyAuthExpiredReasonForOneShot()
    );
  } catch {
    return preserveLegacyAuthExpiredReasonForOneShot();
  }
}

/**
 * Non-destructive storage-only read for auth gates that must not promote a
 * stale legacy URL query into a fresh expired-session block. The legacy query
 * is still stripped so a live-session gate cannot leave a sticky URL behind.
 */
export function peekStoredAuthExpiredReason(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const reason = window.sessionStorage.getItem(AUTH_EXPIRED_REASON_KEY);
    stripLegacyAuthExpiredReasonFromUrl();
    return reason;
  } catch {
    stripLegacyAuthExpiredReasonFromUrl();
    return null;
  }
}

/**
 * Destructive one-shot: returns the stored or legacy URL auth-expired reason
 * and immediately removes it so that subsequent reads (e.g. on reload) return
 * null.
 */
export function takeAuthExpiredReason(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(AUTH_EXPIRED_REASON_KEY);
    if (value !== null) {
      window.sessionStorage.removeItem(AUTH_EXPIRED_REASON_KEY);
      stripLegacyAuthExpiredReasonFromUrl();
      return value;
    }
    return consumeLegacyAuthExpiredReasonFromUrl();
  } catch {
    return consumeLegacyAuthExpiredReasonFromUrl();
  }
}

export async function renewForgeCloudSession(): Promise<boolean> {
  try {
    const endpoint = resolvePrimaryEnvironmentHttpUrl("/api/auth/session/renew");
    if (!endpoint.startsWith("https://")) return false;
    const response = await fetch(endpoint, {
      credentials: "include",
      method: "POST",
    });
    return response.ok;
  } catch {
    return false;
  }
}

export async function redirectToForgeLoginAfterAuthExpired(): Promise<void> {
  if (await renewForgeCloudSession()) {
    clearCloudAuthFailure();
    window.location.reload();
    return;
  }
  await logoutPrimaryHttpSession().catch(() => undefined);
  clearCloudAuthFailure();
  storeAuthRedirectReason("session-expired");
  const loginUrl = new URL(resolvePrimaryEnvironmentHttpUrl("/login"));
  // Keep chrome-less embed mode when the iframe is inside CMMD /forge.
  // A bare /login assign would drop ?embed=1; sessionStorage survives, but
  // the next full document load still needs the query for first paint.
  const loginHref = isCmmdEmbedMode()
    ? withCmmdEmbedQuery(loginUrl.toString())
    : loginUrl.toString();
  window.location.assign(loginHref);
}
